import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArrowPathRoundedSquareIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  HomeIcon,
  HomeModernIcon,
  NewspaperIcon,
  ScaleIcon,
  TagIcon,
  TruckIcon,
  WrenchIcon,
} from "@heroicons/react/24/outline";

import { moduleEnabled } from "./tenantModules";
import { tenantLabel } from "./tenantLabels";

export const NAVIGATION_SECTIONS = [
  { id: "overview", name: "Overview" },
  { id: "operations", name: "Operations" },
  { id: "quality", name: "Quality" },
  { id: "catalog", name: "Catalog" },
  { id: "administration", name: "Administration" },
];

const item = (id, name, href, options = {}) => ({
  id,
  name,
  href,
  ...options,
});

function isPathActive(pathname, route) {
  if (!route.href) return false;
  if (pathname === route.href) return true;

  return (route.matchPrefixes ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function applyNavigationState(navigation, pathname) {
  let title;

  const routes = navigation.map((route) => {
    const children = route.children?.map((child) => {
      const current = isPathActive(pathname, child);
      if (current) title = child.name;
      return { ...child, current };
    });
    const current = isPathActive(pathname, route);
    if (current) title = route.name;

    return {
      ...route,
      ...(children ? { children } : {}),
      current,
      open: children?.some((child) => child.current) ?? false,
    };
  });

  return { routes, title };
}

export default function routes(pathname, userType, tenant = null) {
  const isCFIA = userType === 2;
  const isEmployee = userType === 0;
  const standardUser = !isCFIA && !isEmployee;

  const labels = {
    assemblyItems: tenantLabel(tenant, "assembly_items", "BOM items"),
    manufacturedProducts: tenantLabel(
      tenant,
      "manufactured_products",
      "Manufactured",
    ),
    receiving: tenantLabel(tenant, "receiving", "Purchasing"),
    packaging: tenantLabel(tenant, "packaging", "Packaging"),
    orders: tenantLabel(tenant, "orders", "Orders"),
    shipping: tenantLabel(tenant, "shipping", "Shipping"),
    customers: tenantLabel(tenant, "customers", "Customers"),
    suppliers: tenantLabel(tenant, "suppliers", "Suppliers"),
    locations: tenantLabel(tenant, "locations", "Locations"),
  };

  const navigation = [
    {
      ...item("dashboard", "Dashboard", "/dashboard"),
      section: "overview",
      icon: HomeIcon,
      showInMenu: !isCFIA && moduleEnabled(tenant, "dashboard"),
    },
    {
      ...item("export-label", "Export label", "/export-label"),
      section: "overview",
      icon: NewspaperIcon,
      showInMenu: isCFIA,
    },
    {
      id: "receiving",
      name: labels.receiving,
      section: "operations",
      icon: ArchiveBoxArrowDownIcon,
      showInMenu: !isCFIA && moduleEnabled(tenant, "receiving"),
      children: [
        item("purchase-orders", "Purchase Orders", "/purchase-orders", {
          matchPrefixes: ["/purchase-orders"],
        }),
        item(
          "receiving-invoices",
          "Receiving Invoices",
          "/receiving-invoices",
          { matchPrefixes: ["/receiving-invoices"] },
        ),
      ],
    },
    {
      id: "production",
      name: "Production",
      section: "operations",
      icon: HomeModernIcon,
      showInMenu: standardUser && moduleEnabled(tenant, "production"),
      children: [
        item("new-production", "New Production", "/manufacture-product"),
        item("production-history", "History", "/manufactured-products", {
          matchPrefixes: ["/manufactured-products"],
        }),
        item("production-calendar", "Calendar", "/calendar/production"),
      ],
    },
    {
      id: "employee-production",
      name: "Production",
      section: "operations",
      icon: HomeModernIcon,
      showInMenu: isEmployee && moduleEnabled(tenant, "production"),
      children: [
        item(
          "employee-manufacture",
          "Manufacture New",
          "/manufactured-products-employee",
        ),
      ],
    },
    {
      id: "packaging",
      name: labels.packaging,
      section: "operations",
      icon: ScaleIcon,
      showInMenu: !isCFIA && moduleEnabled(tenant, "packaging"),
      children: [
        item("package-new", "Package New", "/packaging-new"),
        item("packaging-history", "History", "/packaging-reports", {
          matchPrefixes: ["/packaging-reports"],
        }),
      ],
    },
    {
      id: "orders",
      name: labels.orders,
      section: "operations",
      icon: NewspaperIcon,
      showInMenu: standardUser && moduleEnabled(tenant, "orders"),
      children: [
        item("create-order", "Create", "/create-order"),
        item("manage-orders", "Manage Orders", "/orders", {
          matchPrefixes: ["/orders"],
        }),
        item("orders-calendar", "Calendar", "/calendar/orders"),
      ],
    },
    {
      id: "shipping",
      name: labels.shipping,
      section: "operations",
      icon: TruckIcon,
      showInMenu: !isCFIA && moduleEnabled(tenant, "shipping"),
      children: [
        item("shipments", "Shipments", "/shippings", {
          matchPrefixes: ["/shippings"],
        }),
        item("returns", "Returns & Credits", "/credit-returns", {
          matchPrefixes: ["/credit-returns"],
        }),
      ],
    },
    {
      id: "inventory",
      name: "Inventory",
      section: "operations",
      icon: ScaleIcon,
      showInMenu: standardUser && moduleEnabled(tenant, "inventory"),
      children: [
        item("raw-material", "Raw Material", "/inventory-raw-material"),
        item(
          "manufactured-inventory",
          labels.manufacturedProducts,
          "/inventory-manufactured-material",
        ),
        item(
          "inventory-pallet-let-down",
          "Pallet let down",
          "/pallet-let-down",
        ),
      ],
    },
    {
      id: "traceability",
      name: "Traceability",
      section: "quality",
      icon: ArrowPathRoundedSquareIcon,
      showInMenu: standardUser && moduleEnabled(tenant, "traceability"),
      children: [
        item(
          "trace-manufactured",
          "Manufactured",
          "/traceability/manufactured",
        ),
        item("trace-supplier", "Supplier", "/traceability/supplier"),
        item(
          "trace-packaging",
          "Packaging Barcode Lookup",
          "/traceability/packaging-barcode",
        ),
        item(
          "trace-receiving",
          "Receiving Item Lookup",
          "/traceability/receiving-item",
        ),
      ],
    },
    {
      id: "qa",
      name: "QA Module",
      section: "quality",
      icon: ChartBarSquareIcon,
      showInMenu: standardUser && moduleEnabled(tenant, "qa"),
      children: [
        item("qa-reports", "Reports", "/qa-reports"),
        item(
          "in-process-monitoring",
          "In process monitoring",
          "/in-process-monitoring",
          { matchPrefixes: ["/in-process-monitoring"] },
        ),
      ],
    },
    {
      ...item("recipes", "Recipes", "/recipes", {
        matchPrefixes: ["/recipes"],
      }),
      section: "catalog",
      icon: ChartPieIcon,
      showInMenu: standardUser,
    },
    {
      ...item("products", "Products", "/my-products", {
        matchPrefixes: ["/my-products"],
      }),
      section: "catalog",
      icon: WrenchIcon,
      showInMenu: standardUser,
    },
    {
      ...item("employee-barcode", "Barcode Extractor", "/barcode-scanner"),
      section: "operations",
      icon: TagIcon,
      showInMenu: !isCFIA && isEmployee,
    },
    {
      id: "setup",
      name: "Setup",
      section: "administration",
      icon: Cog6ToothIcon,
      showInMenu: standardUser,
      children: [
        item("allergens", "Allergens", "/product-allergens", {
          group: "Products",
        }),
        item("setup-barcode", "Barcode Extractor", "/barcode-scanner", {
          group: "System",
        }),
        item("bom-items", labels.assemblyItems, "/bom-items", {
          group: "Products",
        }),
        item("brands", "Brands", "/brands", { group: "Products" }),
        item(
          "certification-types",
          "Certification Types",
          "/certification-types",
          { group: "Products" },
        ),
        item("claims", "Claims", "/product-claims", { group: "Products" }),
        item("customers", labels.customers, "/customers", {
          group: "Business",
        }),
        item("locations", labels.locations, "/locations", {
          group: "Business",
          matchPrefixes: ["/locations"],
        }),
        item("media-manager", "Media Manager", "/media-manager", {
          group: "System",
        }),
        item("preservatives", "Preservatives", "/preservatives", {
          group: "Products",
        }),
        item("price-rules", "Price Rules", "/price-rules", {
          group: "Business",
        }),
        item("suppliers", labels.suppliers, "/suppliers", {
          group: "Business",
        }),
        item("supplier-items", "Supplier items", "/supplier-items", {
          group: "Business",
        }),
        item("product-types", "Product Types", "/product-types", {
          group: "Products",
        }),
        item("tax-rates", "Tax rates", "/tax-rates", { group: "Business" }),
        item("units", "Units of measure", "/unit-of-measures", {
          group: "Products",
        }),
        item("quickbooks", "QuickBooks", "/quickbooks-login", {
          group: "System",
          matchPrefixes: ["/quickbooks"],
        }),
        item("users", "Users", "/users", {
          group: "System",
          matchPrefixes: ["/users"],
        }),
        item("process-statuses", "Process Statuses", "/process-statuses", {
          group: "System",
        }),
      ],
    },
    {
      ...item("employee-pallet", "Pallet let down", "/pallet-let-down"),
      section: "operations",
      icon: ArchiveBoxIcon,
      showInMenu: !isCFIA && isEmployee,
    },
  ];

  return applyNavigationState(
    navigation.filter((route) => route.showInMenu),
    pathname,
  );
}
